import { useI18n, Trans } from '~/common/context';
import { Button, Card, Container, Flex, Text, WizardSteps } from '@wyz/ui';
import {
  IGlobalQuoteDefaultServicePricingFormData,
  IPricingStatus,
  IQuoteServicePricingPolicyFormStep,
} from '~/purchase/types';
import {
  QuoteServiceGeometryStepForm,
  QuoteServiceMountingStepForm,
  useQuoteServicePricingPolicyForm,
} from '~/purchase/components';
import {
  PRICING_STATUS_ENUM,
  QUOTE_SERVICE_PRICING_POLICY_FORM_STEPS,
} from '~/purchase/schemas';
import { QuoteServicePricingPolicyHelpers } from '~/purchase/helpers';
import { usePolicyFormStepper } from '~/purchase/hooks';
import { RecordValues } from '@wyz/types';
import { useState } from 'react';
// eslint-disable-next-line @wyz/module/no-relative-import
import ApplicationStep from './application-step';

type Step = RecordValues<
  typeof QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_INDEX
>;

type Props = {
  onSubmit: (
    formData: IGlobalQuoteDefaultServicePricingFormData,
    status: IPricingStatus,
  ) => void;
  defaultValues?: IGlobalQuoteDefaultServicePricingFormData;
  isSubmitting: boolean;
  policyGroupId?: number;
};

function QuoteDefaultServicePricingPoliciesForm({
  onSubmit,
  defaultValues,
  isSubmitting,
  policyGroupId,
}: Props) {
  const pricingPolicyForm = useQuoteServicePricingPolicyForm();
  const { t } = useI18n();
  const [formState, setFormState] =
    useState<IGlobalQuoteDefaultServicePricingFormData>(
      defaultValues ??
        (QuoteServicePricingPolicyHelpers.getInitialValues({
          vehicleTypeGroups: pricingPolicyForm.vehicleTypeGroups.optionItems,
          t,
        }) as IGlobalQuoteDefaultServicePricingFormData),
    );

  const [currentStepIndex, stepperHandlers] = usePolicyFormStepper<Step>({});
  const currentStepKey =
    QUOTE_SERVICE_PRICING_POLICY_FORM_STEPS[currentStepIndex];

  return (
    <>
      <WizardSteps
        steps={(
          Object.keys(
            QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_INDEX,
          ) as Array<IQuoteServicePricingPolicyFormStep>
        ).map((stepKey) => ({
          id: stepKey,
          label: t(
            QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_LABELS[
              stepKey
            ],
          ),
        }))}
        activeStep={currentStepIndex}
        onSelectStep={() => {}}
      />
      <Container>
        {currentStepIndex ==
          QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_INDEX
            .MOUNTING && (
          <QuoteServiceMountingStepForm
            globalFormData={formState}
            onSubmit={(formData) => {
              stepperHandlers.onNext();
              setFormState((prev) => ({
                ...prev,
                MOUNTING: formData,
              }));
            }}
          />
        )}

        {currentStepIndex ==
          QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_INDEX
            .APPLICATION && (
          <ApplicationStep
            globalFormData={formState}
            onSubmit={(formData) => {
              stepperHandlers.onNext();
              setFormState((prev) => ({ ...prev, APPLICATION: formData }));
            }}
            policyGroupId={policyGroupId}
          />
        )}
        {currentStepIndex ==
          QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_INDEX
            .GEOMETRY_SERVICE && (
          <QuoteServiceGeometryStepForm
            globalFormData={formState}
            onSubmit={(formData, status) => {
              setFormState((prev) => {
                const nextState = { ...prev, GEOMETRY_SERVICE: formData };
                onSubmit(nextState, status);
                return nextState;
              });
            }}
          />
        )}
        <Card className='sticky' style={{ bottom: '5%' }}>
          <Card.Body>
            <Flex justifyContent={'between'}>
              <Button
                type='button'
                id='previous'
                variant='link'
                disabled={
                  currentStepIndex ===
                    QuoteServicePricingPolicyHelpers.FIRST_STEP || isSubmitting
                }
                onClick={stepperHandlers.onPrevious}
              >
                <Trans i18nKey='app.common.button.previous' />
              </Button>
              <Flex className='space-x-2' alignItems='center'>
                <Text heading='sm' color='secondary'>
                  {formState.MOUNTING?.name}
                </Text>
                <Text heading='sm' color='secondary-gray'>
                  <Trans
                    i18nKey='app.common.step_value'
                    values={{
                      stepNumber: currentStepIndex + 1,
                      stepName: t(
                        QuoteServicePricingPolicyHelpers
                          .SERVICE_PRICING_POLICY_STEP_LABELS[currentStepKey],
                      ),
                    }}
                  />
                </Text>
                {currentStepIndex ===
                  QuoteServicePricingPolicyHelpers.LAST_STEP && (
                  <Button
                    id={'save-as-draft'}
                    data-status={PRICING_STATUS_ENUM.DRAFT}
                    variant='secondary'
                    form={currentStepIndex.toString()}
                    type='submit'
                    children={
                      <Trans i18nKey='app.common.button.save_as_draft' />
                    }
                    isLoading={isSubmitting}
                  />
                )}
                <Button
                  id='next'
                  data-status={PRICING_STATUS_ENUM.ENABLED}
                  variant='primary'
                  type='submit'
                  form={currentStepIndex.toString()}
                  children={
                    currentStepIndex <
                    QuoteServicePricingPolicyHelpers.LAST_STEP
                      ? t('app.common.buttons.continue')
                      : t('app.common.button.save')
                  }
                  isLoading={isSubmitting}
                />
              </Flex>
            </Flex>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}

export default QuoteDefaultServicePricingPoliciesForm;
